import React from "react";
import { Redirect } from "wouter";
import {
  useGetMe,
  useGetMyOnboarding,
  getGetMyOnboardingQueryKey,
} from "@workspace/api-client-react";
import { Loader2 } from "lucide-react";

/**
 * Routes agents through the onboarding lifecycle as a state machine. Non-agent
 * roles, established (already-linked) agents, and approved applicants pass
 * straight through to the app. Everyone else is sent to the right surface:
 *   - in_progress / changes_requested -> the editable wizard (/onboarding)
 *   - submitted / rejected            -> the read-only status page
 * The backend remains the source of truth; this only keeps the UX consistent.
 */
export function AgentOnboardingGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: me, isLoading: meLoading } = useGetMe();
  const isAgent = me?.role === "agent";
  // An agent already linked to an agents row is established and never gated.
  const needsOnboarding = isAgent && me?.agentId == null;
  const { data: onboarding, isLoading: onboardingLoading } = useGetMyOnboarding(
    {
      query: {
        enabled: needsOnboarding,
        queryKey: getGetMyOnboardingQueryKey(),
      },
    },
  );

  if (meLoading || (needsOnboarding && onboardingLoading)) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (needsOnboarding && onboarding) {
    const status = onboarding.status;
    if (status === "in_progress" || status === "changes_requested") {
      return <Redirect to="/onboarding" />;
    }
    if (status === "submitted" || status === "rejected") {
      return <Redirect to="/onboarding/status" />;
    }
    // status === "approved" falls through to full app access.
  }

  return <>{children}</>;
}
